import { DlpSecondaryOrderResponse } from 'apiConnector/secondary-market/dlpOrder/model';
import { Custodian } from 'models/custodian';
import { NextWorkflowError } from 'models/NextWorkflowError';
import { makeGetDlpOrderValidationErrors } from 'state/secondaryMarket/dlpOrder/dlpOrder.selectors';
import { secondaryMarketDlpOrderFormFieldChanged } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.actions';
import { LimitValidity, OrderType } from 'state/secondaryMarket/dlpOrder/form/dlpOrderForm.model';
import { type TradeWay } from 'state/secondaryMarket/secondaryMarket.model';
import { fromSecondaryMarket } from 'state/selectors';
import type { Thunk, ThunkDispatch } from 'state/state.common';
import { AppState } from 'state/state.model';
import {
  workspaceTabFrozen,
  workspaceTabRemoved,
  workspaceTabSetBusy,
  workspaceTabUnactivated,
  workspaceTabUnfrozen,
} from 'state/workspaces/workspace.actions';

import {
  secondaryMarketDlpOrderPlacementClose,
  secondaryMarketDlpOrderPlacementFailed,
  secondaryMarketDlpOrderPlacementReceived,
  secondaryMarketDlpOrderPlacementRequested,
  secondaryMarketDlpOrderPlacementStale,
} from './order.actions';

const resetSidePanelIfNeeded = (dispatch: ThunkDispatch, getState: () => AppState, tabId: string) => {
  const state = getState();
  const getOrderValidationErrors = makeGetDlpOrderValidationErrors();
  const getOrderStatus = fromSecondaryMarket.dlpOrder.order.makeGetDlpOrderStatus(getOrderValidationErrors);
  const status = getOrderStatus(state, tabId);

  const isRejected = status === 'ORDER_FAILED';
  const isPlaced = status === 'ORDER_PLACED';

  if (isRejected) {
    dispatch(secondaryMarketDlpOrderPlacementClose(tabId));
    return;
  }

  if (isPlaced) {
    dispatch(secondaryMarketDlpOrderPlacementStale(tabId));
  }
};
export function secondaryMarketDlpOrderPlacementRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const request = fromSecondaryMarket.dlpOrder.form.getPlaceOrderRequest(state, tabId);

    dispatch(workspaceTabFrozen(tabId, 'secondaryMarketQuotes', true));
    dispatch(secondaryMarketDlpOrderPlacementRequested(tabId, request));
  };
}

export function secondaryMarketDlpOrderPlacementFailedThunk(
  tabId: string,
  isTimeout: boolean,
  errorMessage?: string,
  timeout?: number,
  rejections?: readonly NextWorkflowError[],
  orderId?: string,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketDlpOrderPlacementFailed(tabId, isTimeout, errorMessage, timeout, rejections, orderId));
    dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketQuotes', false));
    dispatch(workspaceTabUnfrozen(tabId, 'secondaryMarketQuotes', false));
  };
}

export function secondaryMarketDlpOrderPlacementReceivedThunk(
  tabId: string,
  data: DlpSecondaryOrderResponse,
): Thunk<void> {
  return dispatch => {
    dispatch(secondaryMarketDlpOrderPlacementReceived(tabId, data));
    dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketQuotes', false));
    dispatch(workspaceTabUnfrozen(tabId, 'secondaryMarketQuotes', false));
  };
}

export function secondaryMarketDlpOrderPlacementCloseThunk(tabId: string, closeTab = false): Thunk<void> {
  return (dispatch, _) => {
    dispatch(secondaryMarketDlpOrderPlacementClose(tabId));
    dispatch(workspaceTabUnfrozen(tabId, 'secondaryMarketQuotes', false));
    if (closeTab === true) {
      dispatch(workspaceTabRemoved(tabId, 'secondaryMarketQuotes'));
      dispatch(workspaceTabUnactivated('secondaryMarketQuotes'));
    }
  };
}

export function secondaryMarketDlpOrderFormQuantityChangedThunk(tabId: string, value?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'quantity', value));
  };
}

export function secondaryMarketDlpOrderFormLimitChangedThunk(tabId: string, value?: number): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limit', value));
  };
}

export function secondaryMarketDlpOrderFormLimitDateChangedThunk(tabId: string, value?: string): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitDate', value));
  };
}

export function secondaryMarketDlpOrderFormLimitTimeChangedThunk(tabId: string, value?: string): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitTime', value));
  };
}

export function secondaryMarketDlpOrderFormTradeWayChangedThunk(tabId: string, value: TradeWay): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'tradeWay', value));
  };
}

export function secondaryMarketDlpOrderFormValidityChangedThunk(tabId: string, value?: LimitValidity): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'validity', value));
    if (value !== 'GTD') {
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitDate', undefined));
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitTime', undefined));
    }
  };
}

export function secondaryMarketDlpOrderFormOrderTypeChangedThunk(tabId: string, value: OrderType): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'orderType', value));
    if (value !== 'Limit') {
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limit', undefined));
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'validity', undefined));
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitDate', undefined));
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'limitTime', undefined));
    } else {
      dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'validity', 'DAY'));
    }
  };
}

export function secondaryMarketDlpOrderFormCustodianChangedThunk(tabId: string, value?: Custodian | null): Thunk<void> {
  return (dispatch, getState) => {
    resetSidePanelIfNeeded(dispatch, getState, tabId);
    dispatch(secondaryMarketDlpOrderFormFieldChanged(tabId, 'custodian', value ?? undefined));
  };
}
